import type { NativePushSubscription } from "~/features/push-notifications/schemas";

import { datadogRum } from "@datadog/browser-rum";
import { $path } from "safe-routes";

/**
 * Get the push manager in a way that ensures old browsers (I'm looking at you, Safari <16) don't crash.
 */
export const getPushManager = async () => {
  const reg = await window.navigator.serviceWorker.ready;
  return "pushManager" in reg && !!reg.pushManager ? reg.pushManager : null;
};

/**
 * Get the current subscription from the browser's push manager.
 */
export const getCurrentSubscription = async () => {
  const pushManager = await getPushManager();
  return await pushManager?.getSubscription();
};

/**
 * Mutate the subscription in our database.
 *
 * @param payload - The subscription to mutate. See https://developer.mozilla.org/en-US/docs/Web/API/PushSubscription
 * @param method - The HTTP method to use. Either POST (subscribe) or DELETE (unsubscribe).
 * @param rum - The AWS RUM instance to use for logging.
 */
export const fetchPushSubscription = async (
  payload: {
    subscription: PushSubscriptionJSON | NativePushSubscription;
    employeeId: string;
    action?: "read";
  },
  method: "POST" | "DELETE",
) => {
  try {
    const res = await fetch($path("/api/push-subscription"), {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      datadogRum.addError(
        `${payload.action ?? method}-push-subscription-failed-${res.status}`,
      );
    }

    return res;
  } catch (err) {
    datadogRum.addError(err);
    return { ok: false };
  }
};
